import { readdir, lstat, readFile } from 'node:fs/promises';
import { join } from 'node:path';
import { fork } from 'node:child_process';
import ora from 'ora';
import * as crypto from 'node:crypto';
import * as path from 'path';

var findFiles = async (initialSrcDir, regex) => {
    const filesList = [];
    async function findFilesRec(srcDir) {
        const allFiles = await readdir(srcDir);
        for (const file of allFiles) {
            const filePath = join(srcDir, file);
            const status = await lstat(filePath);
            if (status.isDirectory()) {
                await findFilesRec(filePath);
            }
            const fileName = file.match(regex);
            if (fileName) {
                filesList.push([fileName[1], filePath]);
            }
        }
    }
    await findFilesRec(initialSrcDir);
    return filesList;
};

var runProcess = async ({ processFile, ctx, initMessage, successMessage, failMessage, }) => {
    const spinner = ora(initMessage).start();
    try {
        await promisifyForkedProcess(processFile, ctx);
        spinner.succeed(successMessage);
    }
    catch (error) {
        spinner.fail(failMessage);
        console.error(error);
        // await core.globalCleanup(ctx);
        process.exit(1);
    }
};
function promisifyForkedProcess(filePath, ctx) {
    const forkedProcess = fork(filePath, [], process.env.NODE_ENV === "dev" ? undefined : { silent: true });
    forkedProcess.send({ ctx });
    return new Promise((resolve, reject) => {
        forkedProcess.on("exit", (code) => {
            if (code === 0)
                resolve();
        });
        // @ts-ignore
        forkedProcess.on("message", ({ error }) => {
            if (!error)
                return;
            reject(error);
        });
    });
}

var util;
(function (util) {
    util.assertEqual = (_) => { };
    function assertIs(_arg) { }
    util.assertIs = assertIs;
    function assertNever(_x) {
        throw new Error();
    }
    util.assertNever = assertNever;
    util.arrayToEnum = (items) => {
        const obj = {};
        for (const item of items) {
            obj[item] = item;
        }
        return obj;
    };
    util.getValidEnumValues = (obj) => {
        const validKeys = util.objectKeys(obj).filter((k) => typeof obj[obj[k]] !== "number");
        const filtered = {};
        for (const k of validKeys) {
            filtered[k] = obj[k];
        }
        return util.objectValues(filtered);
    };
    util.objectValues = (obj) => {
        return util.objectKeys(obj).map(function (e) {
            return obj[e];
        });
    };
    util.objectKeys = typeof Object.keys === "function" // eslint-disable-line ban/ban
        ? (obj) => Object.keys(obj) // eslint-disable-line ban/ban
        : (object) => {
            const keys = [];
            for (const key in object) {
                if (Object.prototype.hasOwnProperty.call(object, key)) {
                    keys.push(key);
                }
            }
            return keys;
        };
    util.find = (arr, checker) => {
        for (const item of arr) {
            if (checker(item))
                return item;
        }
        return undefined;
    };
    util.isInteger = typeof Number.isInteger === "function"
        ? (val) => Number.isInteger(val) // eslint-disable-line ban/ban
        : (val) => typeof val === "number" && Number.isFinite(val) && Math.floor(val) === val;
    function joinValues(array, separator = " | ") {
        return array.map((val) => (typeof val === "string" ? `'${val}'` : val)).join(separator);
    }
    util.joinValues = joinValues;
    util.jsonStringifyReplacer = (_, value) => {
        if (typeof value === "bigint") {
            return value.toString();
        }
        return value;
    };
})(util || (util = {}));
var objectUtil;
(function (objectUtil) {
    objectUtil.mergeShapes = (first, second) => {
        return {
            ...first,
            ...second, // second overwrites first
        };
    };
})(objectUtil || (objectUtil = {}));
util.arrayToEnum([
    "string",
    "nan",
    "number",
    "integer",
    "float",
    "boolean",
    "date",
    "bigint",
    "symbol",
    "function",
    "undefined",
    "null",
    "array",
    "object",
    "unknown",
    "promise",
    "void",
    "never",
    "map",
    "set",
]);

const ZodIssueCode = util.arrayToEnum([
    "invalid_type",
    "invalid_literal",
    "custom",
    "invalid_union",
    "invalid_union_discriminator",
    "invalid_enum_value",
    "unrecognized_keys",
    "invalid_arguments",
    "invalid_return_type",
    "invalid_date",
    "invalid_string",
    "too_small",
    "too_big",
    "invalid_intersection_types",
    "not_multiple_of",
    "not_finite",
]);
class ZodError extends Error {
    get errors() {
        return this.issues;
    }
    constructor(issues) {
        super();
        this.issues = [];
        this.addIssue = (sub) => {
            this.issues = [...this.issues, sub];
        };
        this.addIssues = (subs = []) => {
            this.issues = [...this.issues, ...subs];
        };
        const actualProto = new.target.prototype;
        if (Object.setPrototypeOf) {
            // eslint-disable-next-line ban/ban
            Object.setPrototypeOf(this, actualProto);
        }
        else {
            this.__proto__ = actualProto;
        }
        this.name = "ZodError";
        this.issues = issues;
    }
    format(_mapper) {
        const mapper = _mapper ||
            function (issue) {
                return issue.message;
            };
        const fieldErrors = { _errors: [] };
        const processError = (error) => {
            for (const issue of error.issues) {
                if (issue.code === "invalid_union") {
                    issue.unionErrors.map(processError);
                }
                else if (issue.code === "invalid_return_type") {
                    processError(issue.returnTypeError);
                }
                else if (issue.code === "invalid_arguments") {
                    processError(issue.argumentsError);
                }
                else if (issue.path.length === 0) {
                    fieldErrors._errors.push(mapper(issue));
                }
                else {
                    let curr = fieldErrors;
                    let i = 0;
                    while (i < issue.path.length) {
                        const el = issue.path[i];
                        const terminal = i === issue.path.length - 1;
                        if (!terminal) {
                            curr[el] = curr[el] || { _errors: [] };
                            // if (typeof el === "string") {
                            //   curr[el] = curr[el] || { _errors: [] };
                            // } else if (typeof el === "number") {
                            //   const errorArray: any = [];
                            //   errorArray._errors = [];
                            //   curr[el] = curr[el] || errorArray;
                            // }
                        }
                        else {
                            curr[el] = curr[el] || { _errors: [] };
                            curr[el]._errors.push(mapper(issue));
                        }
                        curr = curr[el];
                        i++;
                    }
                }
            }
        };
        processError(this);
        return fieldErrors;
    }
    static assert(value) {
        if (!(value instanceof ZodError)) {
            throw new Error(`Not a ZodError: ${value}`);
        }
    }
    toString() {
        return this.message;
    }
    get message() {
        return JSON.stringify(this.issues, util.jsonStringifyReplacer, 2);
    }
    get isEmpty() {
        return this.issues.length === 0;
    }
    flatten(mapper = (issue) => issue.message) {
        const fieldErrors = {};
        const formErrors = [];
        for (const sub of this.issues) {
            if (sub.path.length > 0) {
                const firstEl = sub.path[0];
                fieldErrors[firstEl] = fieldErrors[firstEl] || [];
                fieldErrors[firstEl].push(mapper(sub));
            }
            else {
                formErrors.push(mapper(sub));
            }
        }
        return { formErrors, fieldErrors };
    }
    get formErrors() {
        return this.flatten();
    }
}
ZodError.create = (issues) => {
    const error = new ZodError(issues);
    return error;
};

const resolveDeepestIssue = (issue) => {
    if (issue.code === ZodIssueCode.invalid_union && issue.unionErrors.length) {
        const deepestUnionError = issue.unionErrors[issue.unionErrors.length - 1];
        return deepestUnionError.issues[deepestUnionError.issues.length - 1];
    }
    return issue;
};
const errorFormatter = (issues) => {
    const errors = [];
    for (const raw of issues) {
        const issue = resolveDeepestIssue(raw);
        const path = formatErrorPath(issue.path);
        const message = issue.message;
        errors.push(`"${path}": ${message}`);
    }
    return errors;
};
const formatErrorPath = (path) => {
    let formatted = "";
    for (const pathElement of path) {
        if (formatted.length === 0) {
            formatted = `${pathElement}`;
        }
        else {
            formatted +=
                typeof pathElement === "number"
                    ? `[${pathElement}]`
                    : `.${pathElement}`;
        }
    }
    return formatted;
};

/**
 *  Get the hash of the content string. It returns the first 5 characters of the hash
 *  Example: getContentHash("Hello World")
 * @param contentString  The content string to hash
 * @returns
 */
const getContentHash = (contentString) => {
    return crypto
        .createHash("md5")
        .update(contentString)
        .digest("hex")
        .substring(0, 5);
};

const loadJson = async (filePath) => {
    const data = await readFile(filePath, "utf-8");
    return JSON.parse(data);
};

const getComponentLibraryConfig = (componentLibrary) => {
    let libraryName = componentLibrary;
    let include;
    const exclude = [];
    if (typeof componentLibrary === "object" && componentLibrary !== null) {
        libraryName = componentLibrary.name;
        if (componentLibrary.include) {
            include = [...componentLibrary.include];
        }
        if (componentLibrary.exclude) {
            exclude.push(...componentLibrary.exclude);
        }
    }
    return { libraryName, include, exclude };
};
const EXTERNAL_LIBRARY_META_FILE_NAME = "embeddable-components.json";
const EXTERNAL_LIBRARY_GLOBAL_HOOKS_META_NAME = "globalHooks.json";
const getComponentLibraryMeta = async (ctx, componentLibrary) => {
    var _a;
    const { libraryName, include, exclude } = getComponentLibraryConfig(componentLibrary);
    const libraryMeta = (await loadJson(getLibraryPath(ctx, libraryName, EXTERNAL_LIBRARY_META_FILE_NAME)));
    const filterItems = (items) => {
        let result = items;
        if (include) {
            result = result.filter((item) => include.includes(item));
        }
        return result.filter((item) => !exclude.includes(item));
    };
    return {
        ...libraryMeta,
        components: filterItems(libraryMeta.components),
        editors: filterItems(libraryMeta.editors),
        previews: filterItems((_a = libraryMeta.previews) !== null && _a !== void 0 ? _a : []),
    };
};
const getGlobalHooksMeta = async (ctx, libraryName) => {
    return (await loadJson(getLibraryPath(ctx, libraryName, EXTERNAL_LIBRARY_GLOBAL_HOOKS_META_NAME)));
};
const getLibraryPath = (ctx, libraryName, fileName) => path.resolve(ctx.client.rootDir, "node_modules", libraryName, "dist", fileName);

export { EXTERNAL_LIBRARY_GLOBAL_HOOKS_META_NAME, EXTERNAL_LIBRARY_META_FILE_NAME, errorFormatter, findFiles, formatErrorPath, getComponentLibraryConfig, getComponentLibraryMeta, getContentHash, getGlobalHooksMeta, loadJson, resolveDeepestIssue, runProcess };
//# sourceMappingURL=index.esm.js.map
